export type CustomerApiFieldErrors = Record<string, string[]>;

export type CustomerFormFieldErrors = Partial<
  Record<
    'email' | 'firstName' | 'lastName' | 'middleName' | 'aljexCompanyId',
    string
  >
>;

export class CustomerMutationError extends Error {
  readonly fieldErrors: CustomerApiFieldErrors;

  constructor(message: string, fieldErrors: CustomerApiFieldErrors = {}) {
    super(message);
    this.name = 'CustomerMutationError';
    this.fieldErrors = fieldErrors;
  }
}

function readFieldErrors(value: unknown): CustomerApiFieldErrors {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};

  return Object.fromEntries(
    Object.entries(value).flatMap(([field, messages]) => {
      if (!Array.isArray(messages)) return [];

      const validMessages = messages.filter(
        (message): message is string =>
          typeof message === 'string' && message.trim() !== ''
      );

      return validMessages.length > 0 ? [[field, validMessages]] : [];
    })
  );
}

export function toCustomerMutationError(
  payload: unknown,
  fallbackMessage: string
) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return new CustomerMutationError(fallbackMessage);
  }

  const body = payload as Record<string, unknown>;
  const message =
    typeof body.message === 'string' && body.message.trim() !== ''
      ? body.message
      : fallbackMessage;

  return new CustomerMutationError(message, readFieldErrors(body.errors));
}

function joinUniqueMessages(...messageGroups: Array<string[] | undefined>) {
  const messages = [...new Set(messageGroups.flatMap((group) => group ?? []))];
  return messages.length > 0 ? messages.join(' ') : undefined;
}

export function mapCustomerFieldErrors(
  errors: CustomerApiFieldErrors
): CustomerFormFieldErrors {
  return {
    // The customers API derives username from email, so both errors belong to
    // the single Email control in this form.
    email: joinUniqueMessages(errors.email, errors.username),
    firstName: joinUniqueMessages(errors.first_name),
    lastName: joinUniqueMessages(errors.last_name),
    middleName: joinUniqueMessages(errors.middle_name),
    aljexCompanyId: joinUniqueMessages(errors.aljex_company_id),
  };
}
